// The language we communicate with the user in, persisted on their account
// (`up_users.locale`) so emails and other off-app messages match the UI.
export type UserLocale = 'pt' | 'en';

const SUPPORTED: readonly UserLocale[] = ['pt', 'en'];
const EXPLICIT_CHOICE_KEY = 'autoclipper.localeChosen';

export const toUserLocale = (language: string | null | undefined): UserLocale | null => {
  if (!language) return null;
  const base = language.toLowerCase().split(/[-_]/)[0];
  return (SUPPORTED as readonly string[]).includes(base) ? (base as UserLocale) : null;
};

// i18next caches its *detected* language in localStorage too, so we keep a
// separate flag for "the user picked this language in the switcher".
export const hasExplicitLocaleChoice = (): boolean => {
  try {
    return localStorage.getItem(EXPLICIT_CHOICE_KEY) === '1';
  } catch {
    return false;
  }
};

export const markExplicitLocaleChoice = (): void => {
  try {
    localStorage.setItem(EXPLICIT_CHOICE_KEY, '1');
  } catch {
    // Storage unavailable (private mode): the account still gets updated.
  }
};

interface LocaleSyncInput {
  accountLocale: string | null | undefined;
  uiLanguage: string | null | undefined;
  explicitChoice: boolean;
}

interface LocaleSyncDecision {
  save: UserLocale | null; // write to the account
  apply: UserLocale | null; // switch the UI to it
}

// Reconciles the account language with this device's UI language:
// - account empty → save the UI language (backfills OAuth signups);
// - they differ and the user picked the UI language here → the choice wins;
// - they differ otherwise → the account language wins on this device.
export const resolveLocaleSync = ({
  accountLocale,
  uiLanguage,
  explicitChoice,
}: LocaleSyncInput): LocaleSyncDecision => {
  const account = toUserLocale(accountLocale);
  const ui = toUserLocale(uiLanguage);

  if (!account) return { save: ui, apply: null };
  if (ui === account) return { save: null, apply: null };
  if (ui && explicitChoice) return { save: ui, apply: null };
  return { save: null, apply: account };
};
